import * as React from "react";
import {useEffect} from "react";
import {Button, Checkbox, Dropdown, Icon, Input, Modal, Popup, TextArea, Loader, Dimmer} from "semantic-ui-react";
import AceEditor from "react-ace";
import ReactTable from "react-table";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {ChangeableSplitter} from "../../../../util/table-splitter-ui/src/components/Splitters/ChangableSplitter";
import {
    Configuration,
    DisableMonitoringRequest,
    InternalMonitoringApi,
    MonitoringRecordRequest
} from "../../../../../generated-internal-monitoring/index";
import ServiceTypeEnum = MonitoringRecordRequest.ServiceTypeEnum;

// Frontend-only types for UI state management
enum ProcessingType {
    UNTILCHANGE = "UNTILCHANGE",
    UNTILTOMORROW = "UNTILTOMORROW", 
    FORAWEEK = "FORAWEEK",
    FORAMONTH = "FORAMONTH",
    CUSTOMDATE = "CUSTOMDATE"
}


const statusOptions = [
    {
        key: ProcessingType.UNTILCHANGE,
        text: 'Until any update in the report',
        value: ProcessingType.UNTILCHANGE
    },
    {
        key: ProcessingType.UNTILTOMORROW,
        text: 'Until Tomorrow',
        value: ProcessingType.UNTILTOMORROW
    },
    {
        key: ProcessingType.FORAWEEK,
        text: 'For a Week',
        value: ProcessingType.FORAWEEK
    },
    {
        key: ProcessingType.FORAMONTH,
        text: 'For a Month',
        value: ProcessingType.FORAMONTH
    },
    {
        key: ProcessingType.CUSTOMDATE,
        text: 'Custom Date',
        value: ProcessingType.CUSTOMDATE
    }
];

export interface CustomerReportProps {
    columns: any[]
    data: any[]
    currentService: ServiceTypeEnum;
    reFetchReportData: () => Promise<void>
}

export function CustomerReportDynamicSection(props: CustomerReportProps) {
    type FilterMode = "ALL" | "IGNORED" | "ACKNOWLEDGED" | "BOTH" | "UNACK_UNIGNORED";

    // Create Internal Monitoring API client
    const internalMonitoringApi = new InternalMonitoringApi(new Configuration({
        basePath: backend.basePath.replace('/api', '/internalMonitoring') // Remove /api prefix for internal monitoring
    }));

    const [statusModal, setStatusModal] = React.useState<{
        open: boolean;
        type: "IGNORE" | "ACKNOWLEDGE";
    }>({ open: false, type: "IGNORE" });
    const [json, setJson] = React.useState(null)
    const [index, setIndex] = React.useState(null)
    const [selectedOption, setSelectedOption] = React.useState<ProcessingType | null>(null);
    const [fromDate, setFromDate] = React.useState<Date | null>(null);
    const [toDate, setToDate] = React.useState<Date | null>(null);
    const [reason, setReason] = React.useState<string>("");
    const [filteredData, setFilteredData] = React.useState<any[]>([]);
    const [filterMode, setFilterMode] = React.useState<FilterMode>("ALL");
    const [selectedCompanies, setSelectedCompanies] = React.useState<string[]>([])
    const [selectedCompanyObjects, setSelectedCompanyObjects] = React.useState<any[]>([])
    const [isLoading, setIsLoading] = React.useState<boolean>(false)

    const normalizedData = React.useMemo(() => {
        return (props.data ?? []).map((row: any) => {
            const { acknowledgementStatus, ignoreStatus, ...rest } = row || {};
            return {
                ...rest,
                acknowledgementStatus,
                ignoreStatus,
            };
        });
    }, [props.data]);


    useEffect(() => {
        const filtered = normalizedData.filter((c: any) => {
            const ignored = c.ignoreStatus?.enabled === true;
            const acknowledged = c.acknowledgementStatus?.enabled === true;

            switch (filterMode) {
                case "IGNORED":
                    return ignored;
                case "ACKNOWLEDGED":
                    return acknowledged;
                case "BOTH":
                    return ignored && acknowledged;
                case "UNACK_UNIGNORED":
                    return !ignored && !acknowledged;
                case "ALL":
                default:
                    return true;
            }
        });

        setFilteredData(filtered);
    }, [normalizedData, filterMode, props.currentService]);


    const canIgnore =
        selectedCompanies.length > 0 &&
        selectedCompanyObjects.every(c => !c.ignoreStatus?.enabled);

    const canAcknowledgeNow =
        selectedCompanies.length > 0 &&
        selectedCompanyObjects.every(c => !c.acknowledgementStatus?.enabled);

    const canUnignore =
        selectedCompanies.length > 0 &&
        selectedCompanyObjects.every(c => c.ignoreStatus?.enabled);

    const canUnacknowledge =
        selectedCompanies.length > 0 &&
        selectedCompanyObjects.every(c => c.acknowledgementStatus?.enabled);

    const isMixed =
        selectedCompanies.length > 0 &&
        (
            !canIgnore &&
            !canUnignore &&
            !canAcknowledgeNow &&
            !canUnacknowledge
        );

    const handleCheckboxChange = (companyId: string, checked: boolean, companyObject: any) => {
        if (checked) {
            setSelectedCompanies([...selectedCompanies, companyId]);
            setSelectedCompanyObjects([...selectedCompanyObjects, companyObject]);
        } else {
            setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
            setSelectedCompanyObjects(selectedCompanyObjects.filter((company) => company.companyId !== companyId));
        }
    }

    const handleOpenStatusModal = (type: "IGNORE" | "ACKNOWLEDGE") => {
        setStatusModal({ open: true, type });
        setSelectedOption(null);
        setFromDate(null);
        setToDate(null);
        setReason("");
    };

    const handleCloseStatusModal = () => {
        setStatusModal(prev => ({ ...prev, open: false }));
    };


    // Helper function to map service types
    const mapServiceTypeToInternalMonitoring = (serviceType: ServiceTypeEnum): string => {
        switch (serviceType) {
            case ServiceTypeEnum.APIGATEWAYREPORT: return "API_GATEWAY_REPORT";
            case ServiceTypeEnum.SFTPREPORT: return "SFTP_REPORT";
            case ServiceTypeEnum.CUSTOMERFILEHEALTHREPORT: return "CUSTOMER_FILE_HEALTH_REPORT";
            case ServiceTypeEnum.LOGENIOSFILEHEALTHREPORT: return "LOGENIOS_FILE_HEALTH_REPORT";
            case ServiceTypeEnum.ATTACHMENTFILEHEALTHREPORT: return "ATTACHMENT_FILE_HEALTH_REPORT";
            case ServiceTypeEnum.QUEUEREPORT: return "QUEUE_REPORT";
            case ServiceTypeEnum.QUEUEERRORREPORT: return "QUEUE_ERROR_REPORT";
            case ServiceTypeEnum.STATUSJSONREPORT: return "STATUS_JSON_REPORT";
            case ServiceTypeEnum.NEEDSVERIFICATIONREPORT: return "NEEDS_VERIFICATION_REPORT";
            case ServiceTypeEnum.ERRORFILESREPORT: return "ERROR_FILES_REPORT";
            case ServiceTypeEnum.ETACALCULATORREPORT: return "ETA_CALCULATOR_REPORT";
            case ServiceTypeEnum.MESSAGELOGREPORT: return "MESSAGE_LOG_REPORT";
            default: return "CUSTOMER_FILE_HEALTH_REPORT"; // fallback
        }
    };

    // Helper function to calculate until date
    const calculateUntilDate = (selectedOption?: ProcessingType): Date | undefined => {
        if (selectedOption === ProcessingType.CUSTOMDATE) {
            return toDate ?? undefined;
        }
        if (selectedOption === ProcessingType.UNTILTOMORROW) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow;
        }
        if (selectedOption === ProcessingType.FORAWEEK) {
            const nextWeek = new Date();
            nextWeek.setDate(nextWeek.getDate() + 7);
            return nextWeek;
        }
        if (selectedOption === ProcessingType.FORAMONTH) {
            const nextMonth = new Date();
            nextMonth.setMonth(nextMonth.getMonth() + 1);
            return nextMonth;
        }
        return undefined; // For UNTILCHANGE or no option
    };

    // Helper function to clean company objects for until change storage
    // Removes ignoreStatus and acknowledgementStatus to prevent nested object issues
    const cleanCompanyObjectsForUntilChange = (companyObjects: any[]): any[] => {
        return companyObjects.map(company => {
            const cleaned = { ...company };
            // Remove the monitoring status fields that cause nested object issues
            delete cleaned.ignoreStatus;
            delete cleaned.acknowledgementStatus;
            return cleaned;
        });
    };

    async function handleUpdateCustomerReportStatus(params: {
        type: "IGNORE" | "ACKNOWLEDGE";
        enable: boolean;
        reason?: string;
        selectedOption?: ProcessingType;
        from?: Date | null;
    }) {
        const {
            type,
            enable,
            reason = "",
            selectedOption = undefined,
            from = null
        } = params;

        setIsLoading(true);
        try {
            let auth = backend.withTokenAuthHeader(authentication.token)
            if (enable) {
                // Add monitoring record for all selected companies in a single API call
                const payload: MonitoringRecordRequest = {
                    companyIds: selectedCompanies, // Send all companies at once
                    serviceType: mapServiceTypeToInternalMonitoring(props.currentService) as MonitoringRecordRequest.ServiceTypeEnum,
                    state: type === "ACKNOWLEDGE" ? MonitoringRecordRequest.StateEnum.ACK : MonitoringRecordRequest.StateEnum.IGNORE,
                    reason: reason,
                    createdBy: authentication.user.email ?? "",
                    fromDate: from ? from : undefined,
                    untilDate: calculateUntilDate(selectedOption),
                    untilChangeResult: selectedOption === ProcessingType.UNTILCHANGE
                        ? JSON.stringify(cleanCompanyObjectsForUntilChange(selectedCompanyObjects))
                        : undefined
                };

                const response = await internalMonitoringApi.addMonitoringRecord(payload, auth);
                
                // Check if all companies were processed successfully
                if (response.results) {
                    const failedCompanies = response.results.filter(r => !r.success);
                    if (failedCompanies.length > 0) {
                        console.warn(`Some companies failed to update:`, failedCompanies);
                        // You might want to show a notification to the user here
                    }
                }
            } else {
                // Disable monitoring for all selected companies in a single API call
                const disablePayload: DisableMonitoringRequest = {
                    companyIds: selectedCompanies, // Send all companies at once
                    serviceType: mapServiceTypeToInternalMonitoring(props.currentService) as DisableMonitoringRequest.ServiceTypeEnum,
                    disabledBy: authentication.user.email ?? "",
                    state: type === "ACKNOWLEDGE" ? DisableMonitoringRequest.StateEnum.ACK : DisableMonitoringRequest.StateEnum.IGNORE
                };

                const response = await internalMonitoringApi.disableMonitoring(disablePayload, auth);
                
                // Check if all companies were processed successfully
                if (response.results) {
                    const failedCompanies = response.results.filter(r => !r.success);
                    if (failedCompanies.length > 0) {
                        console.warn(`Some companies failed to disable:`, failedCompanies);
                        // You might want to show a notification to the user here
                    }
                }
            }

            await props.reFetchReportData();
        } catch (err) {
            console.error("Error updating customer report status:", err);
        } finally {
            setIsLoading(false);
            handleCloseStatusModal();
        }
    }


    const getRowStyle = (
        ignoreStatus?: any,
        acknowledgementStatus?: any,
        isSelected?: boolean
    ) => {
        const isIgnored = ignoreStatus?.enabled;
        const isAcked = acknowledgementStatus?.enabled;

        let background = 'white';
        if (isIgnored && isAcked) background = '#FFF3CD';       // amber
        else if (isAcked) background = '#b7f4a9';                // green
        else if (isIgnored) background = '#FFEAEA';              // red
        else if (isSelected) background = '#00afec';             // blue highlight

        let color = 'black';
        if (isSelected) {
            if (isIgnored && isAcked) color = '#856404';           // amber text
            else if (isAcked) color = '#155724';                   // green text
            else if (isIgnored) color = '#222';                    // dark gray
            else color = 'white';                                  // for dark bg
        }

        return { background, color, fontWeight: isSelected ? 'bold' : 'normal' };
    };


    function drawTable() {
        return <ReactTable
            data={filteredData}
            columns={[
                {
                    id: 'select',
                    Header: () => <div>Select</div>,
                    accessor: (d: any) => (
                        <Checkbox
                            checked={selectedCompanies.includes(d.companyId)}
                            onChange={(e, {checked}) => handleCheckboxChange(d.companyId, checked, d)}
                        />
                    ),
                    width: 100,
                },
                ...props.columns
            ]}
            defaultPageSize={25}
            className="-striped -highlight"
            getTrProps={(state, rowInfo) => {
                if (!rowInfo) return {};

                const { ignoreStatus, acknowledgementStatus } = rowInfo.original;
                const isSelected = rowInfo.index === index;

                const rowStyle = getRowStyle(ignoreStatus, acknowledgementStatus, isSelected);

                return {
                    onClick: () => {
                        setJson(JSON.stringify(rowInfo.original, null, 4));
                        setIndex(rowInfo.index);
                    },
                    style: {
                        ...rowStyle
                    }
                };
            }}

            style={{cursor: "pointer"}}
        />
    }

    function drawEditor() {
        return <AceEditor
            theme="monokai"
            mode='groovy'
            value={json}
            width="100%"
            height="100%"
            readOnly={true}
        />
    }

    // console.log("CustomerReportDynamicSection - ", props.data)
    if (!props.data) {
        return <div>no data</div>
    }
    const handleStatusSubmit = async () => {
        await handleUpdateCustomerReportStatus({
            type: statusModal.type,
            enable: true,
            reason,
            selectedOption,
            from: fromDate
        });
    };


    return <>
        <Dimmer.Dimmable dimmed={isLoading}>
            <Dimmer active={isLoading} inverted>
                <Loader size="large">
                    {isLoading ? "Processing..." : ""}
                </Loader>
            </Dimmer>
            
            <div
                style={{
                    marginBottom: '15px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
            <div style={{ position: 'relative', marginBottom: '12px' }}>
                <div
                    style={{
                        display: 'flex',
                        gap: '1.5rem',
                        fontWeight: 'bold',
                        position: 'relative',
                    }}
                >
                    {[
                        { label: "Show All", mode: "ALL" },
                        { label: "Ignored by dev", mode: "IGNORED", color: "#d9534f" },
                        { label: "Ignored by pm", mode: "ACKNOWLEDGED", color: "#218838" },
                        { label: "Ignored by dev AND pm", mode: "BOTH", color: "#856404" },
                        { label: "Neither ignored by dev Nor pm", mode: "UNACK_UNIGNORED", color: "#0275d8" },
                    ].map((item) => (
                        <span
                            key={item.mode}
                            onClick={() => setFilterMode(item.mode as FilterMode)}
                            style={{
                                cursor: 'pointer',
                                color: item.color ?? 'black',
                                position: 'relative',
                                paddingBottom: '4px',
                            }}
                        >
                {item.label}
            </span>
                    ))}
                </div>

                <div
                    style={{
                        position: 'absolute',
                        height: '3px',
                        backgroundColor: '#2185d0',
                        transition: 'all 0.3s ease',
                        bottom: 0,
                        left: (() => {
                            switch (filterMode) {
                                case "ALL": return '0%';
                                case "IGNORED": return '13%';
                                case "ACKNOWLEDGED": return '24%';
                                case "BOTH": return '43%';
                                case "UNACK_UNIGNORED": return '72%';
                                default: return '0%';
                            }
                        })(),
                        width: '30px',
                    }}
                />
            </div>



            <div style={{ display: 'flex', alignItems: 'center' }}>
                {canIgnore && !isMixed && (
                    <Popup
                        content="Ignore for dev"
                        trigger={
                            <span>
                    <Button
                        icon
                        onClick={() => handleOpenStatusModal("IGNORE")}
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        <Icon name="ban" />
                    </Button>
                </span>
                        }
                    />
                )}

                {canAcknowledgeNow && !isMixed && (
                    <Popup
                        content="Ignore for pm"
                        trigger={
                            <span>
                    <Button
                        icon
                        onClick={() => handleOpenStatusModal("ACKNOWLEDGE")}
                        style={{ marginLeft: '0.5rem' }}
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        <Icon name="thumbs up" />
                    </Button>
                </span>
                        }
                    />
                )}

                {canUnignore && !isMixed && (
                    <Popup
                        content="Un-ignore for dev"
                        trigger={
                            <span>
                    <Button
                        icon
                        color="red"
                        onClick={() =>
                            handleUpdateCustomerReportStatus({ type: "IGNORE", enable: false })
                        }
                        style={{ marginLeft: '0.5rem' }}
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        <Icon name="undo" />
                    </Button>
                </span>
                        }
                    />
                )}

                {canUnacknowledge && !isMixed && (
                    <Popup
                        content="Un-ignore for pm"
                        trigger={
                            <span>
                    <Button
                        icon
                        color="yellow"
                        onClick={() =>
                            handleUpdateCustomerReportStatus({ type: "ACKNOWLEDGE", enable: false })
                        }
                        style={{ marginLeft: '0.5rem' }}
                        disabled={isLoading}
                        loading={isLoading}
                    >
                        <Icon name="undo" />
                    </Button>
                </span>
                        }
                    />
                )}
            </div>


        </div>

        <ChangeableSplitter
            name={"CustomerReport_" + props.currentService.toString()}
            topOrLeft={drawTable()}
            bottomOrRight={drawEditor()}
        />

        <Modal open={statusModal.open} onClose={handleCloseStatusModal}>
            <Modal.Header>
                {statusModal.type === "IGNORE" ? "Ignore Selected Companies for dev" : "Ignore Selected Companies for pm"}
            </Modal.Header>
            <Modal.Content>
                <Dropdown
                    placeholder="Select Option"
                    fluid
                    selection
                    options={statusOptions}
                    value={selectedOption ?? undefined}
                    onChange={(e, { value }) => setSelectedOption(value as ProcessingType)}
                />

                {selectedOption === ProcessingType.CUSTOMDATE && (
                    <>
                        <div style={{ marginTop: 20 }}>
                            <Input
                                label="From"
                                type="date"
                                value={fromDate ? fromDate.toISOString().split("T")[0] : ""}
                                onChange={(e) => setFromDate(e.target.value ? new Date(e.target.value) : null)}
                            />
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <Input
                                label="To"
                                type="date"
                                value={toDate ? toDate.toISOString().split("T")[0] : ""}
                                onChange={(e) => setToDate(e.target.value ? new Date(e.target.value) : null)}
                            />
                        </div>
                    </>
                )}

                <div style={{ marginTop: 20 }}>
                    <TextArea
                        style={{ width: "100%" }}
                        rows={3}
                        value={reason}
                        onChange={(e) => setReason((e.target as HTMLTextAreaElement).value)}
                        placeholder={`Reason for ${statusModal.type.toLowerCase()}...`}
                    />
                </div>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleCloseStatusModal} disabled={isLoading}>Cancel</Button>
                <Button 
                    primary 
                    onClick={handleStatusSubmit} 
                    disabled={!selectedOption || isLoading}
                    loading={isLoading}
                >
                    {statusModal.type === "IGNORE" ? "Ignore for dev" : "Ignore for pm"}
                </Button>
            </Modal.Actions>
        </Modal>
        </Dimmer.Dimmable>
    </>

}

export function healthyHighlighter(healthy: boolean) {
    // console.log("healthyHighlighter - ", healthy)
    if (healthy) {
        return <Icon name={'heartbeat'} color={'green'}/>
    } else {
        return <Icon name={'ambulance'} color={'red'}/>
    }
}

export function statusHighlighter(status) {
    if (status == "SUCCESS" || status == "OK") {
        return <Icon name={'heartbeat'} color={'green'}/>
    } else {
        if (status == "WARNING") {
            return <Icon name={'ambulance'} color={'yellow'}/>
        } else {
            return <Icon name={'ambulance'} color={'red'}/>
        }
    }
}

export function lifecycleStatusDisplay(company: any) {
    if (!company.lifecycleStatus) {
        return null;
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case "PRODUCTION":
                return "green";
            case "SETUP":
                return "orange";
            case "TESTING":
                return "blue";
            default:
                return "grey";
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "PRODUCTION":
                return "check circle";
            case "SETUP":
                return "wrench";
            case "TESTING":
                return "flask";
            default:
                return "question";
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return null;
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString() + " " + date.toLocaleTimeString();
        } catch {
            return dateStr;
        }
    };

    const tooltipContent = (
        <div>
            <div><strong>Status:</strong> {company.lifecycleStatus}</div>
            {company.lifecycleStatusDescription && (
                <div style={{ marginTop: "5px" }}>
                    <strong>Description:</strong> {company.lifecycleStatusDescription}
                </div>
            )}
            {company.lifecycleStatusProductionDueDate && (
                <div style={{ marginTop: "5px" }}>
                    <strong>Production Due Date:</strong> {formatDate(company.lifecycleStatusProductionDueDate)}
                </div>
            )}
        </div>
    );

    return (
        <Popup
            content={tooltipContent}
            trigger={
                <span>
                    <Icon 
                        name={getStatusIcon(company.lifecycleStatus)} 
                        color={getStatusColor(company.lifecycleStatus)}
                        style={{ cursor: "help" }}
                    />
                </span>
            }
        />
    );
}